import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from './api.service';
import { Pedido, OrderStatus, Devolucion, DevolucionStatus } from './mock-data.service';

type Row = { [key: string]: any };

@Injectable({ providedIn: 'root' })
export class PedidosService {
  private api = inject(ApiService);

  pedidos      = signal<Pedido[]>([]);
  devoluciones = signal<Devolucion[]>([]);
  loading      = signal(false);
  error        = signal('');
  pagination   = signal({ page: 1, page_size: 50, total: 0, total_pages: 1 });

  // Dashboard: todos los pedidos sin items + top productos pre-agregados
  dashboardPedidos          = signal<Pedido[]>([]);
  dashboardTopProductos     = signal<{ nombre: string; qty: number; ingresos: number }[]>([]);
  dashboardDevPendientes    = signal(0);
  dashboardLoading          = signal(false);

  puntosConfig = signal({ normal: 1, aliado: 2, partner: 3 });

  async loadConfig() {
    try {
      const data: Row[] = await this.api.get('/cp/config-puntos');
      if (!data?.length) return;
      const map = Object.fromEntries(data.map(r => [r['clave'], Number(r['valor'])]));
      this.puntosConfig.set({
        normal:  map['puntos_normal']  ?? 1,
        aliado:  map['puntos_aliado']  ?? 2,
        partner: map['puntos_partner'] ?? 3,
      });
    } catch { }
  }

  async saveConfig(config: { normal: number; aliado: number; partner: number }) {
    await this.api.post('/cp/config-puntos', {
      puntos_normal:  config.normal,
      puntos_aliado:  config.aliado,
      puntos_partner: config.partner,
    });
    this.puntosConfig.set({ ...config });
  }

  async loadDashboard() {
    this.dashboardLoading.set(true);
    try {
      const res: any = await this.api.get('/cp/pedidos?summary=true');
      this.dashboardPedidos.set(((res.pedidos ?? []) as Row[]).map(r => this.mapPedido(r)));
      this.dashboardTopProductos.set((res.top_productos ?? []) as { nombre: string; qty: number; ingresos: number }[]);
      this.dashboardDevPendientes.set(Number(res.dev_pendientes_count ?? 0));
    } catch { }
    this.dashboardLoading.set(false);
  }

  async load(page = 1, q = '', status = 'todos', origen = '') {
    this.loading.set(true);
    this.error.set('');
    try {
      const params: Record<string, string> = { page: String(page) };
      if (q.trim())              params['q']      = q.trim();
      if (status !== 'todos')    params['status']  = status;
      if (origen)                params['origen']  = origen;

      const qs  = new URLSearchParams(params).toString();
      const res: any = await this.api.get(`/cp/pedidos?${qs}`);
      this.pedidos.set(((res.pedidos ?? []) as Row[]).map(r => this.mapPedido(r)));
      this.devoluciones.set(((res.devoluciones ?? []) as Row[]).map(r => this.mapDevolucion(r)));
      if (res.pagination) this.pagination.set(res.pagination);
    } catch (e: any) {
      this.error.set(e?.message ?? 'Error al cargar pedidos.');
    }
    this.loading.set(false);
  }

  private mapPedido(r: Row): Pedido {
    return {
      id:             r['id'],
      clienteNombre:  r['cliente_nombre'],
      clienteId:      r['cliente_id']       ?? undefined,
      clienteTelefono: r['cliente_telefono'] ?? undefined,
      clienteEmail:    r['cliente_email']    ?? undefined,
      fecha:          r['fecha'],
      subtotal:       Number(r['subtotal'] ?? 0),
      pointsUsed:     r['puntos_usados'] ?? 0,
      total:          Number(r['total'] ?? 0),
      status:         r['status'] as OrderStatus,
      metodoPago:     r['metodo_pago'] as 'efectivo' | 'transferencia' | 'tarjeta' | 'mixto',
      pagos:          ((r['pagos'] ?? []) as Row[]).map(pg => ({
        metodo: pg['metodo_pago'] as 'efectivo' | 'transferencia' | 'tarjeta',
        monto: Number(pg['monto'] ?? 0),
      })),
      items:          ((r['items'] ?? r['pedido_items'] ?? []) as Row[]).map(i => ({
        productoId:   i['producto_id'] ?? undefined,
        nombre:       i['nombre'],
        qty:          i['cantidad'] ?? i['qty'] ?? 0,
        precio:       Number(i['precio_unit'] ?? i['precio'] ?? 0),
        color:        i['color'] ?? undefined,
        de_proveedor: i['de_proveedor'] === true || i['de_proveedor'] === 1,
      })),
      direccionEnvio:          r['direccion_envio']          ?? undefined,
      paqueteria:              r['paqueteria']               ?? undefined,
      numeroGuia:              r['numero_guia']              ?? undefined,
      notas:                   r['notas']                    ?? undefined,
      devolucionId:            r['devolucion_id']            ?? undefined,
      cuponCodigo:             r['cupon_codigo']             ?? undefined,
      descuentoCupon:          Number(r['descuento_cupon']   ?? 0),
      puntosGanados:           r['puntos_ganados']           ?? 0,
      stripePaymentIntentId:   r['stripe_payment_intent_id'] ?? undefined,
      metodoEnvio:             r['metodo_envio']             ?? undefined,
      costoEnvio:              Number(r['costo_envio']       ?? 0) || undefined,
      origen:                  (r['origen'] as 'online' | 'pos') ?? 'online',
      sucursalId:              r['sucursal_id']              ?? undefined,
      turnoId:                 r['turno_id']                 ?? undefined,
      atendidoPorAdminId:      r['atendido_por_admin_id']    ?? undefined,
    };
  }

  private mapDevolucion(r: Row): Devolucion {
    return {
      id:             r['id'],
      pedidoId:       r['pedido_id'],
      clienteId:      r['cliente_id'] ?? undefined,
      clienteNombre:  r['cliente_nombre'],
      fecha:          r['fecha'],
      status:         r['status'] as DevolucionStatus,
      items:          ((r['items'] ?? r['devolucion_items'] ?? []) as Row[]).map(i => ({
        productoId: i['producto_id'] ?? undefined,
        nombre: i['nombre'], qty: i['qty'], precio: Number(i['precio']), color: i['color'] ?? undefined,
      })),
      motivo:         r['motivo'],
      notas:          r['notas'] ?? undefined,
      reembolsoTotal: Number(r['reembolso_total'] ?? 0),
      origen:              (r['origen'] as 'online' | 'pos') ?? 'online',
      turnoId:             r['turno_id']              ?? undefined,
      atendidoPorAdminId:  r['atendido_por_admin_id']  ?? undefined,
      metodoReembolso:     r['metodo_reembolso']       ?? undefined,
    };
  }

  async updateStatus(pedidoId: string, status: OrderStatus, extra?: { paqueteria?: string; numero_guia?: string }): Promise<{ ok: boolean; error?: string }> {
    try {
      await this.api.patch('/cp/pedidos/status', { id: pedidoId, status, ...extra });
      this.pedidos.set(this.pedidos().map(p =>
        p.id === pedidoId
          ? { ...p, status, paqueteria: extra?.paqueteria ?? p.paqueteria, numeroGuia: extra?.numero_guia ?? p.numeroGuia }
          : p
      ));
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.error?.error ?? e?.message ?? 'Error' };
    }
  }

  async updateDireccion(pedidoId: string, direccion: string): Promise<void> {
    await this.api.patch('/cp/pedidos/direccion', { id: pedidoId, direccion_envio: direccion || null });
    this.pedidos.set(this.pedidos().map(p =>
      p.id === pedidoId ? { ...p, direccionEnvio: direccion || undefined } : p
    ));
  }

  async createDevolucion(dev: Omit<Devolucion, 'id'>): Promise<{ ok: boolean; id?: string; error?: string }> {
    try {
      const res: any = await this.api.post('/cp/devoluciones', {
        pedido_id:       dev.pedidoId,
        cliente_id:      dev.clienteId,
        cliente_nombre:  dev.clienteNombre,
        fecha:           dev.fecha,
        status:          dev.status,
        motivo:          dev.motivo,
        notas:           dev.notas ?? null,
        reembolso_total: dev.reembolsoTotal,
        items:           dev.items,
      });
      const id = res?.id;
      const newDev: Devolucion = { ...dev, id };
      this.devoluciones.set([newDev, ...this.devoluciones()]);
      this.pedidos.set(this.pedidos().map(p => p.id === dev.pedidoId ? { ...p, devolucionId: id } : p));
      return { ok: true, id };
    } catch (e: any) {
      return { ok: false, error: e?.error?.error ?? e?.message ?? 'Error' };
    }
  }

  async updateDevStatus(devId: string, status: DevolucionStatus): Promise<{ ok: boolean; error?: string }> {
    try {
      await this.api.patch('/cp/devoluciones/status', { id: devId, status });
      this.devoluciones.set(this.devoluciones().map(d => d.id === devId ? { ...d, status } : d));
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.error?.error ?? e?.message ?? 'Error' };
    }
  }
}
